import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import ConfirmDialog from '../components/ConfirmDialog';
import { toast } from '../components/Toast';
import api from '../services/api';
import useUserPermissions from '../hooks/useUserPermissions';

const SERVICE_TYPES = [
  { value: 'recurring', label: 'Recurring' },
  { value: 'punctual',  label: 'Punctual' },
];

const OP_CATEGORIES = [
  { value: 'routine',     label: 'Routine' },
  { value: 'corrective',  label: 'Corrective' },
  { value: 'emergency',   label: 'Emergency' },
  { value: 'inspection',  label: 'Inspection' },
];

const FREQUENCIES = [
  { value: 'daily',       label: 'Daily' },
  { value: '3x_week',     label: '3x / Week' },
  { value: '2x_week',     label: '2x / Week' },
  { value: 'weekly',      label: 'Weekly' },
  { value: 'biweekly',    label: 'Bi-Weekly' },
  { value: 'monthly',     label: 'Monthly' },
  { value: 'bimonthly',   label: 'Bi-Monthly' },
  { value: 'quarterly',   label: 'Quarterly' },
  { value: 'semiannual',  label: 'Semi-Annual' },
];

const EMPTY_FORM = {
  name: '',
  description: '',
  service_type: 'punctual',
  operational_category: 'routine',
  frequency: '',
  default_price: '',
  is_active: true,
};

const ServiceCatalogContent = ({ embedded = false }) => {
  const { canViewPricing } = useUserPermissions();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [filterServiceType, setFilterServiceType] = useState('all');
  const [filterOpCategory, setFilterOpCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin' || user.role === 'super_admin';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (showInactive) params.include_inactive = 'true';
      if (filterServiceType !== 'all') params.service_type = filterServiceType;
      if (filterOpCategory !== 'all') params.operational_category = filterOpCategory;
      const res = await api.get('/service-catalog', { params });
      setItems(res.data.items || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, [showInactive, filterServiceType, filterOpCategory]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const openAdd = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setFormData({
      name: item.name || '',
      description: item.description || '',
      service_type: item.service_type || 'punctual',
      operational_category: item.operational_category || 'routine',
      frequency: item.frequency || '',
      default_price: item.default_price != null ? String(item.default_price) : '',
      is_active: item.is_active !== false,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation: cross-field rules (mirrors backend)
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (formData.service_type === 'recurring' && !formData.frequency) {
      toast.error('Frequency is required for recurring items');
      return;
    }
    if (formData.service_type === 'punctual' && formData.frequency) {
      toast.error('Frequency must be empty for punctual items');
      return;
    }

    let priceToSend = null;
    if (formData.service_type === 'punctual') {
      const p = parseFloat(formData.default_price);
      if (isNaN(p) || p < 0) {
        toast.error('Default price is required and must be >= 0 for punctual items');
        return;
      }
      priceToSend = p;
    } else {
      // Recurring: price is null (comes from contract)
      priceToSend = null;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        service_type: formData.service_type,
        operational_category: formData.operational_category,
        frequency: formData.frequency || null,
        default_price: priceToSend,
        is_active: formData.is_active,
      };
      // local_date/local_time auto-injected by axios interceptor

      if (editing) {
        await api.put(`/service-catalog/${editing.id}`, payload);
        toast.success('Item updated');
      } else {
        await api.post('/service-catalog', payload);
        toast.success('Item created');
      }
      setShowModal(false);
      setFormData(EMPTY_FORM);
      setEditing(null);
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save item');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (item) => {
    setConfirmDialog({
      message: `Deactivate "${item.name}"? It will be hidden from selection. Historic records keep referring to it.`,
      confirmText: 'Deactivate',
      icon: '🗄',
      onConfirm: async () => {
        try {
          await api.delete(`/service-catalog/${item.id}`, { data: {} });
          toast.success('Item deactivated');
          setConfirmDialog(null);
          fetchItems();
        } catch (err) {
          toast.error(err?.response?.data?.message || 'Delete failed');
        }
      },
      onCancel: () => setConfirmDialog(null),
    });
  };

  const handleReactivate = async (item) => {
    try {
      await api.put(`/service-catalog/${item.id}`, { is_active: true });
      toast.success('Item reactivated');
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Reactivation failed');
    }
  };

  // Client-side search filter
  const filteredItems = items.filter(it => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      (it.name || '').toLowerCase().includes(t) ||
      (it.description || '').toLowerCase().includes(t)
    );
  });

  const formatCurrency = (v) => {
    if (v == null || v === '') return '—';
    return '$' + parseFloat(v).toFixed(2);
  };

  const formatFrequency = (f) => {
    if (!f) return '—';
    const found = FREQUENCIES.find(x => x.value === f);
    return found ? found.label : f;
  };

  const __wrapStyle = embedded ? { padding: 0 } : styles.container;

  const __content = (
    <div style={__wrapStyle}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Service Catalog</h1>
          <p style={styles.subtitle}>
            Reusable services that can be selected when creating work orders. Recurring items have no fixed price (it comes from the contract). Punctual items have a default price (overridable per WO).
          </p>
        </div>
        {isAdmin && (
          <button onClick={openAdd} style={styles.btnPrimary}>
            + Add Item
          </button>
        )}
      </div>

      <div style={styles.toolbar}>
        <input
          type="text"
          placeholder="Search by name or description..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        <select
          value={filterServiceType}
          onChange={(e) => setFilterServiceType(e.target.value)}
          style={styles.select}
        >
          <option value="all">All Types</option>
          {SERVICE_TYPES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        <select
          value={filterOpCategory}
          onChange={(e) => setFilterOpCategory(e.target.value)}
          style={styles.select}
        >
          <option value="all">All Categories</option>
          {OP_CATEGORIES.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
        {isAdmin && (
          <label style={styles.checkboxLabel}>
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            <span style={{ marginLeft: 6 }}>Show inactive</span>
          </label>
        )}
      </div>

      {loading ? (
        <div style={styles.empty}>Loading...</div>
      ) : filteredItems.length === 0 ? (
        <div style={styles.empty}>
          {items.length === 0
            ? 'No catalog items yet. Click "Add Item" to create one.'
            : 'No items match your filters.'}
        </div>
      ) : (
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr style={styles.thead}>
                <th style={styles.th}>Name</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Frequency</th>
                {canViewPricing && <th style={{...styles.th, textAlign: 'right'}}>Default Price</th>}
                <th style={styles.th}>Status</th>
                {isAdmin && <th style={{...styles.th, textAlign: 'right'}}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filteredItems.map(item => (
                <tr key={item.id} style={styles.tr}>
                  <td style={styles.td}>
                    <strong>{item.name}</strong>
                    {item.description && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>{item.description}</div>
                    )}
                  </td>
                  <td style={styles.td}>
                    <span style={item.service_type === 'recurring' ? styles.badgeRecurring : styles.badgePunctual}>
                      {item.service_type}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={styles.categoryBadge}>{item.operational_category}</span>
                  </td>
                  <td style={{...styles.td, fontSize: 13, color: '#555'}}>
                    {formatFrequency(item.frequency)}
                  </td>
                  {canViewPricing && (
                    <td style={{...styles.td, textAlign: 'right', fontFamily: 'monospace'}}>
                      {item.service_type === 'recurring' ? (
                        <span style={{color: '#888', fontSize: 12, fontStyle: 'italic'}}>per contract</span>
                      ) : (
                        formatCurrency(item.default_price)
                      )}
                    </td>
                  )}
                  <td style={styles.td}>
                    {item.is_active ? (
                      <span style={styles.badgeActive}>Active</span>
                    ) : (
                      <span style={styles.badgeInactive}>Inactive</span>
                    )}
                  </td>
                  {isAdmin && (
                    <td style={{...styles.td, textAlign: 'right'}}>
                      <button onClick={() => openEdit(item)} style={styles.btnSmall}>Edit</button>
                      {item.is_active ? (
                        <button onClick={() => handleDelete(item)} style={styles.btnSmallDanger}>
                          Deactivate
                        </button>
                      ) : (
                        <button onClick={() => handleReactivate(item)} style={styles.btnSmallSecondary}>
                          Reactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ MODAL ═══ */}
      {showModal && (
        <div style={styles.modalBackdrop} onClick={() => !submitting && setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>
              {editing ? 'Edit Item' : 'Add Item'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div style={styles.field}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  autoFocus
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  rows={3}
                  style={{...styles.input, resize: 'vertical', minHeight: 70}}
                />
              </div>

              <div style={styles.row}>
                <div style={{...styles.field, flex: 1}}>
                  <label style={styles.label}>Type *</label>
                  <select
                    value={formData.service_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData(prev => ({
                        ...prev,
                        service_type: newType,
                        // When switching to punctual, clear frequency
                        frequency: newType === 'punctual' ? '' : prev.frequency,
                        // When switching to recurring, clear price
                        default_price: newType === 'recurring' ? '' : prev.default_price,
                      }));
                    }}
                    style={styles.input}
                  >
                    {SERVICE_TYPES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div style={{...styles.field, flex: 1}}>
                  <label style={styles.label}>Operational Category *</label>
                  <select
                    value={formData.operational_category}
                    onChange={(e) => setFormData({...formData, operational_category: e.target.value})}
                    style={styles.input}
                  >
                    {OP_CATEGORIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {formData.service_type === 'recurring' && (
                <div style={styles.field}>
                  <label style={styles.label}>Frequency *</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({...formData, frequency: e.target.value})}
                    required
                    style={styles.input}
                  >
                    <option value="">Select frequency...</option>
                    {FREQUENCIES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    Recurring items have no fixed price — it comes from the customer's contract.
                  </div>
                </div>
              )}

              {formData.service_type === 'punctual' && (
                <div style={styles.field}>
                  <label style={styles.label}>Default Price (USD) *</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.default_price}
                    onChange={(e) => setFormData({...formData, default_price: e.target.value})}
                    required
                    style={styles.input}
                  />
                  <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>
                    This is the suggested price; admin can override per WO.
                  </div>
                </div>
              )}

              {editing && (
                <div style={styles.field}>
                  <label style={styles.checkboxLabel}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    />
                    <span style={{ marginLeft: 8 }}>Active (visible to users)</span>
                  </label>
                </div>
              )}

              <div style={styles.modalActions}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={submitting}
                  style={styles.btnSecondary}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  style={styles.btnPrimary}
                >
                  {submitting ? 'Saving...' : editing ? 'Save Changes' : 'Create Item'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDialog && (
        <ConfirmDialog
          message={confirmDialog.message}
          confirmText={confirmDialog.confirmText}
          confirmColor='#DC2626'
          icon={confirmDialog.icon || '⚠'}
          onConfirm={confirmDialog.onConfirm}
          onCancel={confirmDialog.onCancel}
        />
      )}
    </div>
  );

  return embedded ? __content : <Layout>{__content}</Layout>;
};

// Public page wrapper (used by App.js direct route /service-catalog)
const ServiceCatalog = () => <ServiceCatalogContent embedded={false} />;

export { ServiceCatalogContent };
export default ServiceCatalog;

const styles = {
  container:    { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title:        { margin: 0, color: '#1A3A2A', fontSize: 24 },
  subtitle:     { margin: '4px 0 0', color: '#666', fontSize: 13 },
  toolbar:      { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  searchInput:  { flex: 1, minWidth: 240, padding: '8px 12px', border: '1px solid #DDD', borderRadius: 6, fontSize: 14 },
  select:       { padding: '8px 12px', border: '1px solid #DDD', borderRadius: 6, fontSize: 14, background: 'white' },
  checkboxLabel:{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: 14, color: '#444' },
  empty:        { padding: 40, textAlign: 'center', color: '#888', background: '#FAFAFA', borderRadius: 8 },
  tableWrapper: { background: 'white', borderRadius: 8, overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  table:        { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  thead:        { background: '#F5F7F4' },
  th:           { padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#1A3A2A', borderBottom: '2px solid #E5E5E5', fontSize: 13 },
  tr:           { borderBottom: '1px solid #EEE' },
  td:           { padding: '12px 14px', color: '#333' },
  badgeRecurring: { display: 'inline-block', padding: '3px 10px', background: '#DBEAFE', color: '#1E40AF', borderRadius: 12, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' },
  badgePunctual:  { display: 'inline-block', padding: '3px 10px', background: '#FEF3C7', color: '#92400E', borderRadius: 12, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' },
  categoryBadge:{ display: 'inline-block', padding: '3px 10px', background: '#E8F5E9', color: '#1A3A2A', borderRadius: 12, fontSize: 12, fontWeight: 500, textTransform: 'capitalize' },
  badgeActive:  { display: 'inline-block', padding: '3px 10px', background: '#D1FAE5', color: '#065F46', borderRadius: 12, fontSize: 12, fontWeight: 500 },
  badgeInactive:{ display: 'inline-block', padding: '3px 10px', background: '#F3F4F6', color: '#6B7280', borderRadius: 12, fontSize: 12, fontWeight: 500 },
  btnPrimary:   { background: '#2D5C3F', color: 'white', border: 'none', padding: '10px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  btnSecondary: { background: '#F3F4F6', color: '#374151', border: 'none', padding: '10px 18px', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 500 },
  btnSmall:     { background: '#2D5C3F', color: 'white', border: 'none', padding: '6px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12, marginRight: 6 },
  btnSmallSecondary: { background: '#F3F4F6', color: '#374151', border: 'none', padding: '6px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12 },
  btnSmallDanger:{ background: '#FEE2E2', color: '#991B1B', border: 'none', padding: '6px 12px', borderRadius: 5, cursor: 'pointer', fontSize: 12 },
  modalBackdrop:{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal:        { background: 'white', borderRadius: 10, padding: 24, width: '100%', maxWidth: 560, maxHeight: '90vh', overflow: 'auto' },
  modalTitle:   { margin: '0 0 20px', color: '#1A3A2A', fontSize: 20 },
  field:        { marginBottom: 16 },
  row:          { display: 'flex', gap: 12 },
  label:        { display: 'block', marginBottom: 6, fontWeight: 600, color: '#1A3A2A', fontSize: 13 },
  input:        { width: '100%', padding: '8px 12px', border: '1px solid #DDD', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' },
  modalActions: { display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 },
};
